import QueryString from "query-string";
import { all, call, put, select, takeEvery } from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  addTemplateDataFailure,
  addTemplateDataSuccess,
  deleteFileFailure,
  deleteFileSuccess,
  deleteVersionFailure,
  deleteVersionSuccess,
  getCareSummaryFailure,
  getCareSummarySuccess,
  getCurrentTemplateFailure,
  getCurrentTemplateSuccess,
  getRevisionFailure,
  getRevisionSuccess,
  getObservationsFailure,
  getObservationsSuccess,
  getPreviewFailure,
  getPreviewSuccess,
  getTemplateDataFailure,
  getTemplateDataSuccess,
  getVersionDataFailure,
  getVersionDataSuccess,
  saveObservationFailure,
  saveObservationSuccess,
  saveSnapshotFailure,
  saveSnapshotSuccess,
  saveTemplateDataFailure,
  saveTemplateDataSuccess,
  searchTemplatesFailure,
  searchTemplatesSuccess,
  updateTemplateDataFailure,
  updateTemplateDataSuccess,
  uploadFileFailure,
  uploadFileSuccess,
  viewRevisionFailure,
  viewRevisionSuccess,
  getGovernanceStatsFailure,
  getGovernanceStatsSuccess,
  updatePlanStatusFailure,
  updatePlanStatusSuccess,
} from "./actions";
import {
  ADD_TEMPLATE_DATA,
  DELETE_FILE,
  DELETE_VERSION,
  GET_CARE_SUMMARY,
  GET_CURRENT_TEMPLATE,
  GET_REVISION,
  GET_OBSERVATIONS,
  GET_PREVIEW,
  GET_TEMPLATE_DATA,
  GET_VERSION_DATA,
  SAVE_OBSERVATION,
  SAVE_SNAPSHOT,
  SAVE_TEMPLATE_DATA,
  SEARCH_TEMPLATES,
  UPDATE_TEMPLATE_DATA,
  UPLOAD_FILE,
  VIEW_REVISION,
  GET_GOVERNANCE_STATS,
  UPDATE_PLAN_STATUS,
} from "./constants";

export function* getPreviewSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident/${payload}/response-summary`,
    );
    yield put(getPreviewSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getPreviewFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching network!"));
  }
}

export function* getTemplateDataSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId } = payload;
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident-template-response/${residentId}`,
    );
    yield put(getTemplateDataSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getTemplateDataFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching template data!"));
  }
}

export function* saveTemplateDataSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident-template-response/add`,
      payload,
    );
    yield put(saveTemplateDataSuccess(data));
    yield put(successAlert("Data saved successfully!"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(saveTemplateDataFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while saving template data!"));
  }
}

export function* uploadFileSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident-template-response/upload-document`,
      payload,
    );
    yield put(uploadFileSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(uploadFileFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while uploading file!"));
  }
}

export function* deleteFileSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).delete,
      `/api/resident-template-response/delete-document`,
      payload,
    );
    yield put(deleteFileSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(deleteFileFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while deleting file!"));
  }
}

export function* searchTemplatesSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { residentId, search_term } = payload;
  try {
    let url = `/api/assessment-template/pending-list/${residentId}`;
    if (search_term && search_term.trim()) {
      const queryParams = QueryString.stringify({
        search_term: search_term.trim(),
      });
      url = `${url}?${queryParams}`;
    }
    const { data } = yield call(api(token).get, url);
    yield put(searchTemplatesSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(searchTemplatesFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while searching templates!"));
  }
}

export function* getCareSummarySaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident/${payload}/categorized-assessments`,
    );
    yield put(getCareSummarySuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getCareSummaryFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching care summary!"));
  }
}

export function* getVersionDataSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = QueryString.stringify(payload);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident-template-version?${query}`,
    );
    yield put(getVersionDataSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getVersionDataFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching care summary!"));
  }
}

export function* updateTemplateDataSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident-template-response/update/${payload.id}`,
      payload.data,
    );
    yield put(updateTemplateDataSuccess(data));
    yield put(successAlert("Data updated successfully!"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(updateTemplateDataFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while updating template data!"));
  }
}

export function* saveSnapshotSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/save-resident-tool-snapshort`,
      payload,
    );
    yield put(saveSnapshotSuccess(data));
    yield put(successAlert("Snapshot saved successfully!"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(saveSnapshotFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while saving snapshot!"));
  }
}

export function* getObservationsSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const currentObservations = yield select(
    (state) => state?.ResidentAssessmentReducer?.observations,
  );
  const { resident_id, filters, is_lazy } = payload;
  const query = QueryString.stringify(filters);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/observation-log/${resident_id}?${query}`,
    );
    // Support lazy loading - append data instead of replacing
    if (is_lazy && currentObservations?.data?.length > 0) {
      const mergedData = {
        ...data,
        data: [...currentObservations.data, ...data.data],
      };
      yield put(getObservationsSuccess(mergedData));
    } else {
      yield put(getObservationsSuccess(data));
    }
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getObservationsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching observations!"));
  }
}

export function* deleteVersionSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).delete,
      `/api/resident-template-response/${payload.id}`,
    );
    yield put(deleteVersionSuccess(data));
    yield put(successAlert("Version deleted successfully!"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(deleteVersionFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while deleting version!"));
  }
}

export function* getCurrentTemplateSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident-template-response-details/${payload.carePlanId}`,
    );
    yield put(getCurrentTemplateSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getCurrentTemplateFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching current template!"));
  }
}

export function* saveObservationSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/observation-log/add`,
      payload,
    );
    yield put(saveObservationSuccess(data));
    yield put(successAlert("Observation saved successfully!"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(saveObservationFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while saving observation!"));
  }
}

export function* addTemplateDataSaga({
  payload = { cancelToken: null },
  onSuccess,
}) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident-care-plan/save`,
      payload,
    );
    yield put(addTemplateDataSuccess(data));
    yield put(successAlert("Care plan saved successfully!"));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(addTemplateDataFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while saving care plan!"));
  }
}

export function* getRevisionSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/care-plan-version-history/${payload}`,
    );
    yield put(getRevisionSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getRevisionFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching revision!"));
  }
}

export function* viewRevisionSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const query = QueryString.stringify(payload);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/care-plan-version-revision?${query}`,
    );
    yield put(viewRevisionSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(viewRevisionFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while viewing revision!"));
  }
}

export function* getGovernanceStatsSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  try {
    const { data } = yield call(
      api(token).get,
      `/api/resident/${payload}/governance-item/status-stats`,
    );
    yield put(getGovernanceStatsSuccess(data));
    typeof onSuccess === "function" && onSuccess(data);
  } catch (e) {
    yield put(getGovernanceStatsFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while fetching governance stats!"));
  }
}

export function* updatePlanStatusSaga({ payload, onSuccess }) {
  const token = yield select((state) => state?.AuthReducer?.token);
  const { carePlanId } = payload;
  try {
    const { data } = yield call(
      api(token).post,
      `/api/resident-care-plan/${carePlanId}/status`,
      payload,
    );
    yield put(updatePlanStatusSuccess(data));
    yield put(successAlert("Status updated successfully!"));
    typeof onSuccess === "function" && onSuccess();
  } catch (e) {
    yield put(updatePlanStatusFailure());
    if (typeof e === "string") yield put(errorAlert(e));
    else if (typeof e.error === "string") yield put(errorAlert(e.error));
    else if (e.message !== undefined)
      yield put(errorAlert("Error while updating status!"));
  }
}

export function* getPreviewFlow() {
  yield takeEvery(GET_PREVIEW, getPreviewSaga);
}
export function* getTemplateDataFlow() {
  yield takeEvery(GET_TEMPLATE_DATA, getTemplateDataSaga);
}
export function* saveTemplateDataFlow() {
  yield takeEvery(SAVE_TEMPLATE_DATA, saveTemplateDataSaga);
}
export function* uploadFileFlow() {
  yield takeEvery(UPLOAD_FILE, uploadFileSaga);
}
export function* deleteFileFlow() {
  yield takeEvery(DELETE_FILE, deleteFileSaga);
}
export function* searchTemplatesFlow() {
  yield takeEvery(SEARCH_TEMPLATES, searchTemplatesSaga);
}
export function* getCareSummaryFlow() {
  yield takeEvery(GET_CARE_SUMMARY, getCareSummarySaga);
}
export function* getVersionDataFlow() {
  yield takeEvery(GET_VERSION_DATA, getVersionDataSaga);
}
export function* updateTemplateDataFlow() {
  yield takeEvery(UPDATE_TEMPLATE_DATA, updateTemplateDataSaga);
}
export function* saveSnapshotFlow() {
  yield takeEvery(SAVE_SNAPSHOT, saveSnapshotSaga);
}
export function* getObservationsFlow() {
  yield takeEvery(GET_OBSERVATIONS, getObservationsSaga);
}
export function* deleteVersionFlow() {
  yield takeEvery(DELETE_VERSION, deleteVersionSaga);
}
export function* getCurrentTemplateFlow() {
  yield takeEvery(GET_CURRENT_TEMPLATE, getCurrentTemplateSaga);
}
export function* saveObservationFlow() {
  yield takeEvery(SAVE_OBSERVATION, saveObservationSaga);
}
export function* addTemplateDataFlow() {
  yield takeEvery(ADD_TEMPLATE_DATA, addTemplateDataSaga);
}
export function* getRevisionFlow() {
  yield takeEvery(GET_REVISION, getRevisionSaga);
}
export function* viewRevisionFlow() {
  yield takeEvery(VIEW_REVISION, viewRevisionSaga);
}
export function* getGovernanceStatsFlow() {
  yield takeEvery(GET_GOVERNANCE_STATS, getGovernanceStatsSaga);
}
export function* updatePlanStatusFlow() {
  yield takeEvery(UPDATE_PLAN_STATUS, updatePlanStatusSaga);
}
export default function* ResidentAssessmentSaga() {
  yield all([
    getPreviewFlow(),
    getTemplateDataFlow(),
    saveTemplateDataFlow(),
    uploadFileFlow(),
    deleteFileFlow(),
    searchTemplatesFlow(),
    getCareSummaryFlow(),
    getVersionDataFlow(),
    updateTemplateDataFlow(),
    saveSnapshotFlow(),
    getObservationsFlow(),
    deleteVersionFlow(),
    getCurrentTemplateFlow(),
    saveObservationFlow(),
    addTemplateDataFlow(),
    getRevisionFlow(),
    viewRevisionFlow(),
    getGovernanceStatsFlow(),
    updatePlanStatusFlow(),
  ]);
}
